import { api } from '@/api';
import {
  DataSetFilterTemplateDto,
  DataSetFilterTemplateDtoTypeEnum,
  DataSetRequestDto,
  EnumerationOptionDto,
  EnumerationValueListDto,
  EvaluationParameterDto,
  instanceOfCreateEnumerationValueListDto,
  instanceOfEnumerationOptionDto,
  instanceOfEnumerationValueListDto,
  instanceOfNumericDataSetFilterDto,
  instanceOfNumericValueWithUnitDto,
  instanceOfDiscreteTextValueListDto,
  ISetupParameterFilter,
  NumericDataSetFilterDto,
  NumericValueWithUnitDto,
  TaskTypeDescription,
  DiscreteTextValueListDto,
} from '@/api/generator';
import { useAccountStore } from '@/stores/accountStore';
import { omitDeep } from '@/utils/omitDeep';
import { I18nKey } from '@/utils/typings';
import {
  toCreateEvaluationParameterDto,
  toICreateSetupParameterFilter,
} from '@/views/tasks/mapping';
import { getSetupParameterName } from '@/views/tasks/utils';
import {
  type DataSetFilterTemplateDtoDisplay,
  type EnumerationOptionDtoWithDisplayName,
  type EnumerationValueListDtoWithDisplayName,
  hasDisplayName,
  type ISetupParameterFilterDisplay,
  NumericDataSetFilterDtoWithDisplayName,
  type DiscreteTextValueListDtoWithDisplayValues,
  WithDisplayName,
} from '@/views/TrendsSelectionView/types';
import {
  filterParameterFilters,
  instantiateParameterSearch,
  normalizeSearchText,
  shouldUsePrefix,
} from '@/views/TrendsSelectionView/utils';
import { t } from 'i18next';
import { cloneDeep, includes, isEqual, union, xor } from 'lodash';
import { DateTime } from 'luxon';
import MiniSearch, { SearchResult } from 'minisearch';
import { storeToRefs } from 'pinia';
import { v7 as uuidv7 } from 'uuid';
import { computed, MaybeRef, reactive, readonly, ref, watch } from 'vue';

const startDateDefaultValue = DateTime.now().minus({ month: 1 }).startOf('day');
const endDateDefaultValue = DateTime.now().endOf('day');
const startDate = ref<DateTime | undefined>(startDateDefaultValue);
const endDate = ref<DateTime | undefined>(endDateDefaultValue);

const searchFieldInput = ref<string | undefined>();

const filters = reactive<DataSetFilterTemplateDtoDisplay>({
  availableTaskTypes: [],
  availableSupportSystems: [],
  availableTargetSystems: [],
  targetSystemSetupFilters: [],
  supportSystemSetupFilters: [],
  evaluationParameterFilters: [],
  $type:
    DataSetFilterTemplateDtoTypeEnum.PtwPtw100AppZeroApplicationSharedResultsDtoDataSetFilterTemplateDtoPtwPtw100AppZeroApplicationShared,
});

type FilterMenuEntries = {
  taskTypes: TaskTypeDescription[];
  targetSystems: DataSetFilterTemplateDtoDisplay['availableTargetSystems'];
  supportSystems: DataSetFilterTemplateDtoDisplay['availableSupportSystems'];
  targetSystemSetupParameters: ISetupParameterFilterDisplay[];
  supportSystemSetupParameters: ISetupParameterFilterDisplay[];
  evaluationParameters: DataSetFilterTemplateDtoDisplay['evaluationParameterFilters'];
};

type FilterMenuSelections = {
  taskTypes: TaskTypeDescription[];
  supportSystems: string[];
  targetSystems: string[];
  evaluationParameters: string[];
};

const filterMenuSelections = reactive<FilterMenuSelections>({
  taskTypes: [],
  supportSystems: [],
  targetSystems: [],
  evaluationParameters: [],
});

// parameter search
const miniSearchTargetSystemParameters = ref<MiniSearch>(instantiateParameterSearch());
const miniSearchSupportSystemParameters = ref<MiniSearch>(instantiateParameterSearch());
const miniSearchEvaluationParameters = ref<MiniSearch>(instantiateParameterSearch());

const storedDatasetParameterResult = ref<DataSetFilterTemplateDto>();

export function isOptionSelected(
  setup: EnumerationValueListDto | DiscreteTextValueListDto | NumericDataSetFilterDto,
  value: EnumerationOptionDto | NumericValueWithUnitDto | string,
): boolean {
  if (instanceOfEnumerationValueListDto(setup) && instanceOfEnumerationOptionDto(value)) {
    return !!setup.selectedOptions?.some((o) => o.id === value.id);
  } else if (
    instanceOfNumericDataSetFilterDto(setup) ||
    instanceOfDiscreteTextValueListDto(setup)
  ) {
    return includes(setup.selectedValues, value);
  }
  return false;
}

interface UseDataSetFilteringOptions {
  maximumDataSetCount: MaybeRef<number>;
}

export function useDataSetFiltering(
  options: UseDataSetFilteringOptions = { maximumDataSetCount: 40 },
) {
  const accountStore = useAccountStore();
  const { language } = storeToRefs(accountStore);

  const maximumDataSetCount = ref(options.maximumDataSetCount);

  // filter task types
  const filteredTaskTypes = computed(() =>
    filters.availableTaskTypes.filter((filter) =>
      t(`TaskTypeDescriptionEnum.${filter}`)
        .toLowerCase()
        .includes(searchFieldInput.value?.toLowerCase() ?? ''),
    ),
  );

  // filter support systems
  const filteredSupportSystems = computed(() =>
    filters.availableSupportSystems.filter((filter) =>
      hasDisplayName(filter) && filter.displayName
        ? filter.displayName.toLowerCase().includes(searchFieldInput.value?.toLowerCase() ?? '')
        : filter.name?.toLowerCase().includes(searchFieldInput.value?.toLowerCase() ?? ''),
    ),
  );

  // filter target systems
  const filteredTargetSystems = computed(() =>
    filters.availableTargetSystems.filter((filter) =>
      hasDisplayName(filter) && filter.displayName
        ? filter.displayName.toLowerCase().includes(searchFieldInput.value?.toLowerCase() ?? '')
        : filter.name?.toLowerCase().includes(searchFieldInput.value?.toLowerCase() ?? ''),
    ),
  );

  // filter target system parameters
  const miniSearchTargetSystemParameterResults = ref<SearchResult[]>([]);
  const filteredTargetSystemSetupFilters = computed(() => {
    return filterParameterFilters(
      searchFieldInput.value,
      filters.targetSystemSetupFilters,
      miniSearchTargetSystemParameterResults.value,
    );
  });

  // filter support system parameters
  const miniSearchSupportSystemParameterResults = ref<SearchResult[]>([]);
  const filteredSupportSystemSetupFilters = computed(() => {
    return filterParameterFilters(
      searchFieldInput.value,
      filters.supportSystemSetupFilters,
      miniSearchSupportSystemParameterResults.value,
    );
  });

  // filter evaluation parameters
  const miniSearchEvaluationParameterResults = ref<SearchResult[]>([]);
  const filteredEvaluationParameters = computed(() => {
    const searchInput = searchFieldInput.value;
    if (!searchInput) return filters.evaluationParameterFilters;

    const results = miniSearchEvaluationParameterResults.value;
    if (results.length === 0) return [];

    const matchedIds = results.map((r) => r.id);
    return filters.evaluationParameterFilters.filter((filter) => matchedIds.includes(filter.id));
  });

  const resetSelectedFilters = () => {
    filterMenuSelections.taskTypes = [];
    filterMenuSelections.supportSystems = [];
    filterMenuSelections.targetSystems = [];
    filterMenuSelections.evaluationParameters = [];
    miniSearchSupportSystemParameterResults.value = [];
    searchFieldInput.value = '';

    const clearFilterArray = <T>(filterArray: T[]): T[] => {
      return filterArray.map((entry) => {
        return { ...entry, selectedValues: undefined, selectedOptions: [] };
      });
    };

    filters.targetSystemSetupFilters = clearFilterArray<ISetupParameterFilterDisplay>(
      filters.targetSystemSetupFilters,
    );
    filters.evaluationParameterFilters = clearFilterArray<WithDisplayName<EvaluationParameterDto>>(
      filters.evaluationParameterFilters,
    );
    filters.supportSystemSetupFilters = clearFilterArray<ISetupParameterFilterDisplay>(
      filters.supportSystemSetupFilters,
    );
  };

  const resetFilters = () => {
    filters.availableTaskTypes = [];
    filters.availableSupportSystems = [];
    filters.availableTargetSystems = [];
    filters.targetSystemSetupFilters = [];
    filters.supportSystemSetupFilters = [];
    filters.evaluationParameterFilters = [];

    filterMenuSelections.taskTypes = [];
    filterMenuSelections.supportSystems = [];
    filterMenuSelections.targetSystems = [];
    filterMenuSelections.evaluationParameters = [];
  };

  const resetDates = () => {
    startDate.value = startDateDefaultValue;
    endDate.value = endDateDefaultValue;
  };

  async function initializeFilters() {
    const { result: datasetParameterResult } = await api.dataSetApi.getDataSetFilterTemplate();

    if (
      datasetParameterResult &&
      storedDatasetParameterResult.value &&
      isEqual(
        omitDeep(storedDatasetParameterResult.value, 'creationTime', {
          recursive: true,
        }),
        omitDeep(datasetParameterResult, 'creationTime', { recursive: true }),
      )
    )
      return;
    else {
      storedDatasetParameterResult.value = cloneDeep(datasetParameterResult);
      resetFilters();
    }

    if (datasetParameterResult) {
      filters.availableTaskTypes = datasetParameterResult.availableTaskTypes;
      filters.availableSupportSystems = datasetParameterResult.availableSupportSystems.map((s) => ({
        ...s,
        displayName: `${t(s.name as I18nKey)} | ${s.iuid}`,
      }));
      filters.availableTargetSystems = datasetParameterResult.availableTargetSystems.map((ts) => ({
        ...ts,
        displayName: `${t(ts.name as I18nKey)} | ${ts.iuid}`,
      }));
      filters.targetSystemSetupFilters = datasetParameterResult.targetSystemSetupFilters.map(
        (ts): ISetupParameterFilterDisplay => {
          if (instanceOfEnumerationValueListDto(ts)) {
            return {
              ...ts,
              options: ts.options?.map((o) => {
                return {
                  ...o,
                  displayName: o.name ? t(o.name as I18nKey) : o.name,
                } satisfies EnumerationOptionDtoWithDisplayName;
              }),
              displayName: t(ts.name as I18nKey),
              id: uuidv7(),
            } satisfies EnumerationValueListDtoWithDisplayName;
          } else if (instanceOfNumericDataSetFilterDto(ts)) {
            return {
              ...ts,
              displayValues: ts.availableValues?.map((v) => `${v.value} ${v.unitAbbreviation}`),
              displayName: t(ts.name as I18nKey),
              id: uuidv7(),
            } satisfies NumericDataSetFilterDtoWithDisplayName;
          }
          return {
            ...ts,
            displayValues: ts.availableValues?.map((v) => v),
            displayName: t(ts.name as I18nKey),
            id: uuidv7(),
          } satisfies DiscreteTextValueListDtoWithDisplayValues;
        },
      );
      filters.supportSystemSetupFilters = datasetParameterResult.supportSystemSetupFilters.map(
        (ss): ISetupParameterFilterDisplay => {
          if (instanceOfEnumerationValueListDto(ss)) {
            return {
              ...ss,
              options: ss.options?.map((o) => {
                return {
                  ...o,
                  displayName: o.name ? t(o.name as I18nKey) : o.name,
                } satisfies EnumerationOptionDtoWithDisplayName;
              }),
              displayName: t(ss.name as I18nKey),
              id: uuidv7(),
            } satisfies EnumerationValueListDtoWithDisplayName;
          } else if (instanceOfNumericDataSetFilterDto(ss)) {
            return {
              ...ss,
              displayValues: ss.availableValues?.map((v) => `${v.value} ${v.unitAbbreviation}`),
              displayName: t(ss.name as I18nKey),
              id: uuidv7(),
            } satisfies NumericDataSetFilterDtoWithDisplayName;
          }
          return {
            ...ss,
            displayValues: ss.availableValues?.map((v) => v),
            displayName: t(ss.name as I18nKey),
            id: uuidv7(),
          } satisfies DiscreteTextValueListDtoWithDisplayValues;
        },
      );
      filters.evaluationParameterFilters = datasetParameterResult.evaluationParameterFilters.map(
        (e) => ({
          ...e,
          displayName: `${getSetupParameterName(e)} | ${e.iuid}`,
          id: uuidv7(),
        }),
      );

      const targetSystemParametersWithUniqueIdsAndDisplayName =
        filters.targetSystemSetupFilters.map((filter) => {
          return {
            ...filter,
            displayName: `${getSetupParameterName(filter)} | ${filter.iuid}`,
          };
        });
      miniSearchTargetSystemParameters.value.addAll(
        targetSystemParametersWithUniqueIdsAndDisplayName,
      );

      const supportSystemParametersWithUniqueIdsAndDisplayName =
        filters.supportSystemSetupFilters.map((filter) => {
          return {
            ...filter,
            displayName: `${getSetupParameterName(filter)} | ${filter.iuid}`,
          };
        });
      miniSearchSupportSystemParameters.value.addAll(
        supportSystemParametersWithUniqueIdsAndDisplayName,
      );

      const evaluationParametersWithDisplayName = filters.evaluationParameterFilters.map((e) => ({
        ...e,
        displayName: `${getSetupParameterName(e)} | ${e.iuid}`,
      }));
      miniSearchEvaluationParameters.value.addAll(evaluationParametersWithDisplayName);
    }
  }

  // task types
  function selectTaskType(taskType: TaskTypeDescription) {
    filterMenuSelections.taskTypes = union(filterMenuSelections.taskTypes, [taskType]);
  }
  function deselectTaskType(taskType: TaskTypeDescription) {
    filterMenuSelections.taskTypes = filterMenuSelections.taskTypes.filter((tt) => tt !== taskType);
  }
  function isTaskTypeSelected(taskType: TaskTypeDescription): boolean {
    return filterMenuSelections.taskTypes.includes(taskType);
  }

  // support system
  function selectSupportSystem(supportSystemId: string) {
    filterMenuSelections.supportSystems = union(filterMenuSelections.supportSystems, [
      supportSystemId,
    ]);
  }
  function deselectSupportSystem(supportSystemId: string) {
    filterMenuSelections.supportSystems = filterMenuSelections.supportSystems.filter(
      (ss) => ss !== supportSystemId,
    );
  }
  function isSupportSystemSelected(supportSystemId: string): boolean {
    return filterMenuSelections.supportSystems.includes(supportSystemId);
  }

  // target system
  function selectTargetSystem(targetSystemId: string) {
    filterMenuSelections.targetSystems = union(filterMenuSelections.targetSystems, [
      targetSystemId,
    ]);
  }
  function deselectTargetSystem(targetSystemId: string) {
    filterMenuSelections.targetSystems = filterMenuSelections.targetSystems.filter(
      (ts) => ts !== targetSystemId,
    );
  }
  function isTargetSystemSelected(targetSystemId: string): boolean {
    return filterMenuSelections.targetSystems.includes(targetSystemId);
  }

  // target system setups
  function toggleParameterOption(
    uiFilter: ISetupParameterFilter,
    value: EnumerationOptionDto | NumericValueWithUnitDto | string,
  ) {
    // Resolve the canonical object that lives in the reactive state
    const resolveOriginal = (id?: string) =>
      filters.targetSystemSetupFilters.find((f) => f.id === id) ??
      filters.supportSystemSetupFilters.find((f) => f.id === id);

    // every filter created in `initializeFilters` owns a stable `id`
    const original = resolveOriginal(uiFilter.id) ?? uiFilter;

    // Toggle against that canonical object
    if (instanceOfEnumerationValueListDto(original) && instanceOfEnumerationOptionDto(value)) {
      original.selectedOptions = xor(original.selectedOptions ?? [], [value]);
    } else if (
      instanceOfNumericDataSetFilterDto(original) &&
      instanceOfNumericValueWithUnitDto(value)
    ) {
      original.selectedValues = xor(original.selectedValues ?? [], [value]);
    } else if (instanceOfDiscreteTextValueListDto(original) && typeof value === 'string') {
      original.selectedValues = xor(original.selectedValues ?? [], [value]);
    }
  }

  // evaluation parameters
  function selectEvaluationParameter(evaluationId: string) {
    filterMenuSelections.evaluationParameters = union(filterMenuSelections.evaluationParameters, [
      evaluationId,
    ]);
  }
  function deselectEvaluationParameter(evaluationId: string) {
    filterMenuSelections.evaluationParameters = filterMenuSelections.evaluationParameters.filter(
      (ep) => ep !== evaluationId,
    );
  }
  function isEvaluationParameterSelected(evaluationId: string): boolean {
    return filterMenuSelections.evaluationParameters.includes(evaluationId);
  }

  watch(
    searchFieldInput,
    (newValue) => {
      const terms = normalizeSearchText(newValue ?? '')
        .split(/\s+/u)
        .filter((t) => t.length > 0);
      if (terms.length) {
        const usePrefix = shouldUsePrefix(terms);
        const query = {
          combineWith: 'AND',
          queries: terms,
        } as const;
        const searchOptions = { prefix: usePrefix };

        miniSearchTargetSystemParameterResults.value =
          miniSearchTargetSystemParameters.value.search(query, searchOptions);

        miniSearchSupportSystemParameterResults.value =
          miniSearchSupportSystemParameters.value.search(query, searchOptions);

        miniSearchEvaluationParameterResults.value = miniSearchEvaluationParameters.value.search(
          query,
          searchOptions,
        );
      } else {
        miniSearchTargetSystemParameterResults.value =
          miniSearchTargetSystemParameters.value.search(MiniSearch.wildcard);
        miniSearchSupportSystemParameterResults.value =
          miniSearchSupportSystemParameters.value.search(MiniSearch.wildcard);
        miniSearchEvaluationParameterResults.value = miniSearchEvaluationParameters.value.search(
          MiniSearch.wildcard,
        );
      }
    },
    { immediate: true },
  );

  watch(language, async (languageNew, languageOld) => {
    if (languageNew !== languageOld) {
      miniSearchTargetSystemParameters.value = instantiateParameterSearch();
      miniSearchSupportSystemParameters.value = instantiateParameterSearch();
      miniSearchEvaluationParameters.value = instantiateParameterSearch();
      await initializeFilters();
    }
  });

  const searchFilter = computed<DataSetRequestDto>(() => ({
    startDate: startDate.value?.setZone('UTC').toISO() ?? null,
    endDate: endDate.value?.setZone('UTC').toISO() ?? null,
    maximumDataSetCount: maximumDataSetCount.value ?? null,
    supportSystemIds: filterMenuSelections.supportSystems,
    targetSystemIds: filterMenuSelections.targetSystems,
    selectedTaskTypes: filterMenuSelections.taskTypes,
    supportSystemSetupFilters: filters.supportSystemSetupFilters
      .map((supportSystemSetup) => toICreateSetupParameterFilter(supportSystemSetup))
      .map((supportSystemSetup) => {
        const isEnabled = instanceOfCreateEnumerationValueListDto(supportSystemSetup)
          ? Boolean(supportSystemSetup.selectedOptions?.length)
          : Boolean(supportSystemSetup.selectedValues?.length);
        return { ...supportSystemSetup, isEnabled };
      }),
    targetSystemSetupFilters: filters.targetSystemSetupFilters
      .map((targetSystemsSetup) => toICreateSetupParameterFilter(targetSystemsSetup))
      .map((targetSystemSetup) => {
        const isEnabled = instanceOfCreateEnumerationValueListDto(targetSystemSetup)
          ? Boolean(targetSystemSetup.selectedOptions?.length)
          : Boolean(targetSystemSetup.selectedValues?.length);
        return { ...targetSystemSetup, isEnabled };
      }),
    evaluationParameterFilters: filters.evaluationParameterFilters
      .map((evaluationParameter) => toCreateEvaluationParameterDto(evaluationParameter))
      .map((evaluationParameter) => {
        const isEnabled = filterMenuSelections.evaluationParameters?.includes(
          evaluationParameter.id,
        );
        return { ...evaluationParameter, isEnabled };
      }),
  }));

  const hasChangedDates = computed(() => {
    const isEqual =
      startDate.value?.equals(startDateDefaultValue) && endDate.value?.equals(endDateDefaultValue);
    return !isEqual;
  });

  const hasValidDateRange = computed(
    () => startDate.value && endDate.value && startDate.value <= endDate.value,
  );

  const hasActiveFilters = computed(
    () =>
      filterMenuSelections.taskTypes?.length > 0 ||
      filterMenuSelections.targetSystems?.length > 0 ||
      filterMenuSelections.supportSystems?.length > 0 ||
      filterMenuSelections.evaluationParameters?.length > 0 ||
      filters.targetSystemSetupFilters.some((targetSystemSetup) => {
        if (
          instanceOfNumericDataSetFilterDto(targetSystemSetup) &&
          targetSystemSetup?.selectedValues?.length
        ) {
          return targetSystemSetup.selectedValues.length > 0;
        } else if (
          instanceOfEnumerationValueListDto(targetSystemSetup) &&
          targetSystemSetup?.selectedOptions?.length
        ) {
          return targetSystemSetup.selectedOptions.length > 0;
        } else if (
          instanceOfDiscreteTextValueListDto(targetSystemSetup) &&
          targetSystemSetup?.selectedValues?.length
        ) {
          return targetSystemSetup.selectedValues.length > 0;
        }
      }) ||
      filters.supportSystemSetupFilters.some((supportSystemSetup) => {
        if (
          instanceOfNumericDataSetFilterDto(supportSystemSetup) &&
          supportSystemSetup?.selectedValues?.length
        ) {
          return supportSystemSetup.selectedValues.length > 0;
        } else if (
          instanceOfEnumerationValueListDto(supportSystemSetup) &&
          supportSystemSetup?.selectedOptions?.length
        ) {
          return supportSystemSetup.selectedOptions.length > 0;
        } else if (
          instanceOfDiscreteTextValueListDto(supportSystemSetup) &&
          supportSystemSetup?.selectedValues?.length
        ) {
          return supportSystemSetup.selectedValues.length > 0;
        }
      }),
  );

  const shouldFetchData = computed(() => hasActiveFilters.value || hasValidDateRange.value);

  return {
    initializeFilters,
    resetFilters,
    searchFieldInput,
    startDate,
    endDate,
    filters,
    filterMenuEntries: computed(
      () =>
        ({
          taskTypes: filteredTaskTypes.value,
          targetSystems: filteredTargetSystems.value,
          supportSystems: filteredSupportSystems.value,
          targetSystemSetupParameters: filteredTargetSystemSetupFilters.value,
          supportSystemSetupParameters: filteredSupportSystemSetupFilters.value,
          evaluationParameters: filteredEvaluationParameters.value,
        }) satisfies FilterMenuEntries,
    ),
    filterMenuSelections,
    selectTaskType,
    deselectTaskType,
    isTaskTypeSelected,
    selectTargetSystem,
    deselectTargetSystem,
    isTargetSystemSelected,
    selectSupportSystem,
    deselectSupportSystem,
    isSupportSystemSelected,
    selectEvaluationParameter,
    deselectEvaluationParameter,
    isEvaluationParameterSelected,
    toggleParameterOption,
    resetDates,
    resetSelectedFilters,
    hasActiveFilters,
    hasValidDateRange,
    hasChangedDates,
    shouldFetchData,
    searchFilter,
    maximumDataSetCount: readonly(maximumDataSetCount),
  };
}
